/**
 * Redux duck for Screen 1 — the proctor's AI Interview Waiting screen (`skillfit-01`).
 *
 * THREE thunks of DIFFERENT shapes, and the differences are the design:
 *
 *   - `resolveObserverEntry` — the FULL observer join, two server hops behind one client call.
 *     Runs exactly TWICE per screen lifetime: once on mount (to populate the screen) and once at
 *     advance time (to mint a fresh short-TTL LiveKit credential immediately before it is used).
 *     ⛔ It is never polled: it redeems a single-use handoff token and mints a participant
 *     credential, neither of which is a thing to ask for on a tick.
 *   - `pollObserverReadiness` — status-only, one hop, no mint and no redeem. This is the one on
 *     the 5-second interval, and it carries NO entry fields.
 *   - `fetchInterviewDisplay` — the three fields that only DESCRIBE the interview, asked ONCE and
 *     ⛔ awaited by nothing. ⭐ It is separate from the entry resolve for the reason the whole
 *     screen now turns on: those fields cost two live marketplace lookups, and while they rode the
 *     entry a slow lookup abandoned the credential with them. A recruiter sat outside an interview
 *     that ran to completion without them. ⛔ Never fold it back in, and ⛔ never put it on a tick.
 *
 * Nothing here dispatches a write. All three thunks are reads.
 */

import { earlierIso } from '../../util/skillFitWaitClock';
import { storableError } from '../../util/errors';
import {
  INVITATION_REFUSAL_REPLACED,
  invitationRefusalKind,
} from '../../util/skillFitInvitationRefusal';
import {
  fetchSkillFitInterviewRoomDisplay,
  fetchSkillFitObserverReadiness,
  joinSkillFitInterviewRoomAsObserver,
} from '../../util/api';
import { SESSION_END_STATES, isSessionEnded } from '../../util/aiInterviewSessionEndStates';

// ================ Session status ================ //

// The statuses the proctor can never advance on, read from the one declaration this half keeps so
// a new ending cannot be added to the product and quietly missed here.
export const TERMINAL_SESSION_STATUSES = SESSION_END_STATES;

/**
 * ⚠ A terminal session reaches this screen as a PLAIN 200 body from the readiness endpoint, not as
 * a 409. The poll's stop condition is therefore a status to read, never an exception to catch. The
 * 409 mapping below belongs to the join call alone.
 */
export const isTerminalSessionStatus = isSessionEnded;

// The statuses that mean "the room is open — go in". ⭐ `in_room` is the load-bearing one:
// the candidate's arrival now stops at `in_room` and the interview only reaches `in_progress` when
// somebody presses Start. Advancing on `in_progress` alone would strand the proctor on this screen
// until that press — and the press is a control they are supposed to be IN THE ROOM to make, so
// nobody would ever be there to make it. `in_progress` stays in the list for the proctor who opens
// their link late, after the interview has already begun.
export const ROOM_OPEN_SESSION_STATUSES = ['in_room', 'in_progress'];

export const isRoomOpenSessionStatus = status => ROOM_OPEN_SESSION_STATUSES.includes(status);

// ================ Refusal reasons ================ //

// Five refusals, five messages — ⛔ never collapsed into one generic failure. A proctor told
// "something went wrong" when the real answer is "this interview finished an hour ago" has no next
// action; each of these has a different one.
export const ENTRY_ERROR_NOT_YOURS = 'not-your-link'; // 403
export const ENTRY_ERROR_UNKNOWN_INTERVIEW = 'unknown-interview'; // 404
export const ENTRY_ERROR_ALREADY_ENDED = 'already-ended'; // 409
// The invitation itself has expired, with no ended attempt to report. ⛔ Not the transient bucket:
// the answer cannot change on a later tick, so a poll that read it as a gap would ask forever.
export const ENTRY_ERROR_INVITATION_EXPIRED = 'invitation-expired'; // 410
// A re-interview replaced the invitation. Also a 410, told apart by its code: the proctor's next step
// is the newer invitation already sent, not asking for this one to be re-sent.
export const ENTRY_ERROR_INVITATION_REPLACED = 'invitation-replaced'; // 410, replaced code
// The residual bucket: 5xx, a dropped connection, anything unmapped. Kept as a value of its own
// rather than folded into one of the refusals, so the screen never claims a cause it does not know.
export const ENTRY_ERROR_UNAVAILABLE = 'unavailable';

/**
 * How long the entry resolve may take before it is treated as unanswered.
 *
 * ⚠ **This exists because a browser `fetch` never times out on its own.** A 5xx *or timeout*
 * is already the transient bucket — but nothing produced a timeout, so a server
 * that accepted the connection and then never replied left the proctor on "Checking your
 * invitation…" **forever**: no refusal, no notice, no retry, no way to tell a slow interview from a
 * dead one. Found by walking the screen against a wedged backend (2026-08-10); ⛔ no unit test could
 * have caught it, because a mocked promise always settles.
 *
 * 15s is deliberately generous: the entry resolve is two server hops. A healthy local round trip
 * measured ~1.4s back when the second hop still re-derived the display fields through live
 * marketplace calls; it no longer does. This is the "something is wrong" threshold, ⛔ not a
 * latency budget — ⚠ and it is what abandoned the whole call, credential included, while those
 * lookups ran long. ⛔ Nothing that crosses the marketplace may be put back behind it.
 */
export const ENTRY_REQUEST_TIMEOUT_MS = 15000;

/**
 * Reject if `promise` has not settled within `ms`. The rejection deliberately carries **no
 * `status`**, so it maps through `reasonForStatus`'s default to `ENTRY_ERROR_UNAVAILABLE` — the
 * transient bucket — exactly like a 5xx: the screen shows its quiet "still trying" notice, ⛔ never
 * one of the refusals, and the poll keeps running so a recovered server heals the screen with
 * no action from the proctor.
 */
export const withEntryTimeout = (promise, ms = ENTRY_REQUEST_TIMEOUT_MS) => {
  let timer = null;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const e = new Error('The interview did not answer in time.');
      e.timeout = true;
      reject(e);
    }, ms);
  });
  // ⚠ `finally` rather than a `then`/`catch` pair: the timer must be cleared on the SUCCESS path
  // too, or every resolved request leaves a pending handle that keeps the event loop (and, in a
  // test, fake timers) alive for 15 seconds.
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

const reasonForStatus = status => {
  switch (status) {
    case 403:
      return ENTRY_ERROR_NOT_YOURS;
    case 404:
      return ENTRY_ERROR_UNKNOWN_INTERVIEW;
    case 409:
      return ENTRY_ERROR_ALREADY_ENDED;
    case 410:
      return ENTRY_ERROR_INVITATION_EXPIRED;
    default:
      return ENTRY_ERROR_UNAVAILABLE;
  }
};

/**
 * `src/util/api.js` decorates every 4xx/5xx rejection with `.status` (`decorateApiError`), which is
 * the only field this mapping reads.
 *
 * `after401Retry` handles the refusal mapping's one special case: a 401 means the single-use handoff
 * token expired or was replayed, so the call is retried once — and if the retry 401s again, the
 * proctor is presented with 403 not-your-link. A retry that fails with a DIFFERENT status is
 * mapped by that status, because collapsing a 404 into "not yours" would be exactly the loss of
 * distinction the refusal mapping forbids.
 */
const toEntryError = (e, { after401Retry = false } = {}) => {
  const status = e && e.status;
  const effectiveStatus = status === 401 && after401Retry ? 403 : status;
  const replaced = invitationRefusalKind(e) === INVITATION_REFUSAL_REPLACED;
  return {
    reason: replaced ? ENTRY_ERROR_INVITATION_REPLACED : reasonForStatus(effectiveStatus),
    status: status === undefined ? null : status,
    error: storableError(e),
  };
};

// ================ Action types ================ //

export const RESOLVE_ENTRY_REQUEST = 'app/AIInterviewWaitingPage/RESOLVE_ENTRY_REQUEST';
export const RESOLVE_ENTRY_SUCCESS = 'app/AIInterviewWaitingPage/RESOLVE_ENTRY_SUCCESS';
export const RESOLVE_ENTRY_ERROR = 'app/AIInterviewWaitingPage/RESOLVE_ENTRY_ERROR';
export const POLL_READINESS_SUCCESS = 'app/AIInterviewWaitingPage/POLL_READINESS_SUCCESS';
export const POLL_READINESS_ERROR = 'app/AIInterviewWaitingPage/POLL_READINESS_ERROR';

// The screen's read of the three fields that only DESCRIBE the interview — its own path, its own
// three actions. ⛔ Deliberately not folded into the entry resolve: the whole point is that this can
// fail, or never answer at all, without touching whether the proctor gets into the room.
export const DISPLAY_REQUEST = 'app/AIInterviewWaitingPage/DISPLAY_REQUEST';
export const DISPLAY_SUCCESS = 'app/AIInterviewWaitingPage/DISPLAY_SUCCESS';
export const DISPLAY_ERROR = 'app/AIInterviewWaitingPage/DISPLAY_ERROR';

// ================ Reducer ================ //

const initialState = {
  // The full `GET /join` body the observer entry resolved with:
  // { interviewId, roomName, livekitToken, livekitUrl, role, status, identityVerificationOutcome,
  //   identityVerifiedBy, jobId, candidateName, jobTitle, company, neutralHeadPose,
  //   neutralHeadPoseInForce }.
  // `livekitToken` / `livekitUrl` are held here unused by this screen — they are the room entry's
  // input. Every display field is individually nullable, and the no-session-yet body nulls all
  // four credential/id fields with status
  // 'not_started' — that is the WAIT state, ⛔ never a refusal.
  entry: null,
  // The wait clock, as the readiness poll reports it. Two flat, nullable fields, and both describe
  // THIS observer: `holdExpiresAt` is when this observer's own wait runs out, `holdExpired` says it
  // already has. ⛔ The candidate's wait is deliberately NOT held here. Each party is released on
  // their own clock and is shown the instant that governs them; a party who reopens their link
  // starts a new sitting and moves their own instant only, so there is no longer a single shared
  // moment for this screen to name.
  // ⚠ Both describe the WAIT ONLY — past `not_started` the candidate has arrived and an expiry
  // reached afterwards means nothing (see `selectWaitEnded`).
  holdExpiresAt: null,
  holdExpired: false,
  // ⭐ The OTHER clock over this screen, and ⛔ NOT a wait: it does not describe the wait. It bounds
  // how long both parties may sit in the room before the interview is started, it begins when the
  // first of them enters, and it can end the attempt while the second is still on this screen.
  prepWindowExpiresAt: null,
  // The interviewToken `entry` was resolved for. The room screen reads the held entry across a
  // client-side navigation, and the store outlives that navigation — so the entry must carry
  // proof of WHICH interview it belongs to. Without it, a proctor who opened two invitations in one
  // page load could be handed the other interview's credentials, and `Room.connect` would succeed.
  entryToken: null,
  // The interviewToken the screen last asked about, set when the ask begins rather than when it
  // succeeds. A different token means a different invitation, and the wait clock above belongs to
  // the one before it — see RESOLVE_ENTRY_REQUEST.
  requestedToken: null,
  // The freshest status either call reported. The poll normally owns it; the entry call seeds it.
  sessionStatus: null,
  // Set by the FIRST resolve only — the advance-time resolve must not flip a fully rendered screen
  // back into its loading treatment.
  entryInFlight: false,
  // { reason, status, error } or null.
  entryError: null,
  /*
   * ⭐ The three fields that only DESCRIBE the interview — the candidate's name, the job title and
   * the employer — as this screen's own read resolved them. `null` until that read lands, which is
   * what the summary line and the top bar render their em-dashes from.
   *
   * ⛔ NOT part of `entry`, and never merged into it. The entry is what admits the proctor to the
   * room; these are what the screen says about the interview while they wait. They used to be the
   * same answer, and a slow lookup for these three cosmetic facts is what held a recruiter outside
   * an interview that ran without them.
   */
  display: null,
  /*
   * ⭐ When a readiness poll last SUCCEEDED, as an ISO string, or `null` before the first one has.
   *
   * ⛔ This is not a health flag and not a duration. A browser that freezes or discards this tab
   * runs no code, so nothing in here can notice and nothing can update — but the tab keeps painting
   * its last frame. This value is what that frozen frame still says, and because it is an absolute
   * instant it goes on being checkable against any clock in the room long after the screen died.
   */
  lastContactAt: null,
};

/*
 * Answers that arrive for an invitation the screen is no longer asking about. Every async result
 * carries the token it was requested for; once a different invitation has been opened, a late answer
 * for the previous one would write that invitation's status, clock or name over the new one.
 * An action with no token is taken as current.
 */
const TOKEN_KEYED_RESULTS = [
  'app/AIInterviewWaitingPage/RESOLVE_ENTRY_SUCCESS',
  'app/AIInterviewWaitingPage/RESOLVE_ENTRY_ERROR',
  'app/AIInterviewWaitingPage/POLL_READINESS_SUCCESS',
  'app/AIInterviewWaitingPage/POLL_READINESS_ERROR',
  'app/AIInterviewWaitingPage/DISPLAY_SUCCESS',
  'app/AIInterviewWaitingPage/DISPLAY_ERROR',
];
const isForAnotherInvitation = (state, action) =>
  TOKEN_KEYED_RESULTS.includes(action.type) &&
  !!action.meta &&
  action.meta.token !== undefined &&
  state.requestedToken != null &&
  action.meta.token !== state.requestedToken;

export default function aiInterviewWaitingPageReducer(state = initialState, action = {}) {
  const { type, payload, meta } = action;
  if (isForAnotherInvitation(state, action)) return state;
  switch (type) {
    case RESOLVE_ENTRY_REQUEST: {
      const token = meta && meta.token !== undefined ? meta.token : state.requestedToken;
      /*
       * ⚠ A second invitation opened in the same page load starts from nothing. Everything in this
       * slice describes one invitation, and several fields deliberately survive a thin or failed
       * tick — an ended wait never reverts, a status is never blanked — so without this reset the
       * next invitation would open showing the last one's answers: already released, or closed.
       * ⛔ The same token asked again — the advance-time resolve — keeps everything.
       */
      const changed = state.requestedToken != null && token !== state.requestedToken;
      if (changed) {
        return { ...initialState, requestedToken: token, entryInFlight: true };
      }
      return {
        ...state,
        requestedToken: token,
        entryInFlight: !!(payload && payload.initial),
        entryError: null,
      };
    }
    case RESOLVE_ENTRY_SUCCESS:
      return {
        ...state,
        entryInFlight: false,
        entryError: null,
        entry: payload,
        // Rides `meta`, not `payload` — `payload` IS the join body, and the token is the client's own
        // knowledge of which door it opened, never a field the server sent back.
        entryToken: meta && meta.token !== undefined ? meta.token : state.entryToken,
        // `status` is nullable in the join body. A call that reported no status does not erase one
        // an earlier call did report.
        sessionStatus: payload && payload.status != null ? payload.status : state.sessionStatus,
      };
    case RESOLVE_ENTRY_ERROR:
      // ⛔ Does not clear `entry`: a failed advance-time re-mint must not blank a screen that is
      // already showing the session it resolved on mount.
      return { ...state, entryInFlight: false, entryError: payload };
    case POLL_READINESS_SUCCESS:
      // Status and the wait clock. ⛔ No entry fields — the readiness body carries none, so the
      // resolved entry never moves here.
      // ⚠ Each field falls back to what the last poll reported rather than to null: a tick that
      // omits the clock is a gap in the answer, not an answer, and blanking the only deadline on
      // screen because of one thin response is the failure this screen already avoids for status.
      return {
        ...state,
        entryError: null,
        sessionStatus: payload && payload.status != null ? payload.status : state.sessionStatus,
        holdExpiresAt:
          payload && payload.holdExpiresAt != null ? payload.holdExpiresAt : state.holdExpiresAt,
        /*
         * ⚠ One-way within a page: once the wait has ended it stays ended. A tick on which the
         * server could not read presence still answers `holdExpired: false`, and taking that as news
         * would put a released proctor back on the waiting copy until the next tick restored it.
         */
        holdExpired: !!state.holdExpired || !!(payload && payload.holdExpired === true),
        /*
         * ⭐ The OTHER clock over a waiting party, and the one that actually ended an attempt this
         * screen had promised five more minutes of. It bounds how long both parties may sit in the
         * room before the interview is started, and it starts when the FIRST of them enters — so it
         * runs while the second is still here.
         */
        prepWindowExpiresAt:
          payload && payload.prepWindowExpiresAt != null
            ? payload.prepWindowExpiresAt
            : state.prepWindowExpiresAt,
        // Same per-field fallback as the clocks above, for the same reason: an action arriving
        // without an instant is a gap in the answer, not an answer.
        lastContactAt: action.receivedAt != null ? action.receivedAt : state.lastContactAt,
      };
    case POLL_READINESS_ERROR:
      // ⛔ A failed poll leaves the resolved entry — and the last known status — on screen.
      // ⛔ It also leaves `lastContactAt` where it is. A failed attempt is not contact, and a stamp
      // that advanced on one would assert the screen is healthy at the exact moment it is not.
      return { ...state, entryError: payload };
    case DISPLAY_REQUEST:
      return state;
    case DISPLAY_SUCCESS:
      return { ...state, display: payload || null };
    case DISPLAY_ERROR:
      // ⛔ The fields are NOT cleared, and this is ⛔ NOT `entryError`. A read that failed knows
      // nothing an earlier one did not, and blanking a name the proctor is already reading would
      // turn a retry into a flicker — a watcher confirming they have the right person would see the
      // answer withdrawn. Nor may it reach the refusal treatment: nobody is being refused anything.
      return state;
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const resolveEntryRequest = (initial, token) => ({
  type: RESOLVE_ENTRY_REQUEST,
  payload: { initial: !!initial },
  meta: { token },
});
export const resolveEntrySuccess = (payload, token) => ({
  type: RESOLVE_ENTRY_SUCCESS,
  payload,
  meta: { token },
});
export const resolveEntryError = (payload, token) => ({
  type: RESOLVE_ENTRY_ERROR,
  error: true,
  payload,
  meta: { token },
});
/*
 * ⭐ `receivedAt` is stamped HERE, ⛔ never inside the reducer. The readiness body carries no time
 * field, so the stamp has to be a reading of the browser's own clock — and a reducer that reads a
 * clock is impure and forces every test that touches this arm to mock one. A defaulted argument
 * gives production a stamp for free and gives tests an exact instant to assert against.
 *
 * ⭐ The browser's clock is also the RIGHT clock. The observer compares this value against a wall
 * clock beside them; a server instant would be correct in absolute terms and useless the moment the
 * two disagree.
 */
export const pollReadinessSuccess = (payload, receivedAt = new Date().toISOString(), token) => ({
  type: POLL_READINESS_SUCCESS,
  payload,
  receivedAt,
  meta: { token },
});
export const pollReadinessError = (payload, token) => ({
  type: POLL_READINESS_ERROR,
  error: true,
  payload,
  meta: { token },
});

// ================ Selectors ================ //

// Defensive against an unregistered slice: a connected screen reading `undefined` for every prop
// is the failure mode `reducers.js` warns about, and these must degrade rather than throw.
const sliceOf = state => (state && state.AIInterviewWaitingPage) || {};

export const selectObserverEntry = state => sliceOf(state).entry || null;
/**
 * When THIS observer's wait actually ends, as an ISO string, or `null` while there is nothing
 * usable to name.
 *
 * ⛔ Never the candidate's expiry, and ⛔ never the earlier of the two. Each party is released on
 * their own clock, so the only instant this observer can act on is their own — and once a party can
 * restart their wait by reopening their link, the two are not even measured from the same event.
 * Naming the candidate's would state a moment that releases somebody else.
 */
export const selectWaitEndsAt = state => {
  const slice = sliceOf(state);
  return earlierIso(
    slice.holdExpiresAt || null,
    /*
     * ⭐ Still in the comparison, and ⛔ not a wait. The expiry above bounds how long this observer
     * is held waiting; this bounds how long the pair may sit in the room before starting, and it is
     * enforced independently. It really does end the attempt, so a screen naming only the later of
     * the two states a release that will not happen.
     */
    slice.prepWindowExpiresAt || null
  );
};

/**
 * Whether the wait is over and the observer should be told so rather than left on a spinner.
 *
 * ⚠ Gated on the status still being `not_started`. The expiry describes the WAIT: once the status
 * has moved on the candidate did arrive, and an expiry reached afterwards is stale news — acting on
 * it would strand an observer whose interview is actually running.
 */
export const selectWaitEnded = state => {
  const slice = sliceOf(state);
  const status = slice.sessionStatus != null ? slice.sessionStatus : null;
  return Boolean(slice.holdExpired) && (status === null || status === 'not_started');
};

export const selectSessionStatus = state => {
  const status = sliceOf(state).sessionStatus;
  return status === undefined ? null : status;
};
/**
 * The three describing fields, or `null` while the screen has not learned them.
 *
 * ⛔ `null` is not an error — it is "not yet", and it is the state the summary line's em-dashes
 * exist for. The screen is fully usable in it, which is the entire reason the read was taken off
 * the entry path.
 */
export const selectInterviewDisplay = state => sliceOf(state).display || null;

export const selectEntryInFlight = state => !!sliceOf(state).entryInFlight;
export const selectEntryError = state => sliceOf(state).entryError || null;
// `null` until the first poll succeeds — and the screen renders no stamp at all in that state
// rather than a placeholder, which would be indistinguishable from a real reading gone stale.
export const selectLastContactAt = state => sliceOf(state).lastContactAt || null;

/** The role the join body carries for a proctor — minted server-side by the proctor handoff. */
export const OBSERVER_ROLE = 'observer';

/**
 * The observer entry the room screen may enter on — the credentials
 * the waiting screen already resolved, handed across the navigation rather than re-requested.
 *
 * Four conditions, and ⛔ every one of them is load-bearing:
 *
 *   - `entryToken === interviewToken` — the store outlives client-side navigation, so an entry from
 *     a different invitation must never answer for this URL.
 *   - `role === 'observer'` — the ONLY observer marker, and it is the SERVER's word (the proctor
 *     handoff mints it, `internalSessions.routes.js:109` serializes it). ⛔ Never infer the role
 *     from the route, the referrer or the presence of a slice: a candidate must not be able to
 *     reach observer treatment by any client-side means.
 *   - both credential halves present — a not-yet-started session nulls `livekitToken` and
 *     `livekitUrl` together, and half of a `Room.connect(url, token)` pair is not a
 *     credential. Entering on one would be a guaranteed-failing connect.
 *
 * Returns null rather than a partial: the caller's fallback is the observer handoff, which is
 * the right answer for every case this rejects.
 */
export const selectHeldObserverEntry = (state, interviewToken) => {
  const slice = sliceOf(state);
  const entry = slice.entry;
  if (!entry || !interviewToken || slice.entryToken !== interviewToken) return null;
  if (entry.role !== OBSERVER_ROLE) return null;
  if (!entry.livekitToken || !entry.livekitUrl) return null;
  return entry;
};

// ================ Thunks ================ //

/**
 * Resolve the proctor's observer entry — the full join, via the proctor handoff.
 *
 * Dispatched TWICE per screen lifetime by design: on mount, and again on advance so the LiveKit
 * participant token is minted immediately before `Room.connect` uses it (the token's TTL is 10
 * minutes and a proctor may wait far longer). Both server hops are idempotent under repeat calls —
 * hop 1 mints a fresh single-use handoff token each time, and `sessionService.getOrCreate` matches
 * on `interviewToken` — which is what makes the second call correct. ⛔ It is NOT a licence to poll
 * it; `pollObserverReadiness` is the call on the interval.
 *
 * "First" is derived from state, not from an argument: whichever call runs while no entry has been
 * resolved yet owns the loading treatment. That keeps the caller's signature to the token alone and
 * makes the property structural rather than a flag a call site could get wrong.
 *
 * Resolves with the entry body, or `null` on refusal. It deliberately does NOT re-throw: this
 * screen renders its refusals (`SF-01-ENTRY-ERROR`), and there is no navigation for a caller to
 * guard with a `.catch`.
 */
export const resolveObserverEntry = interviewToken => (dispatch, getState) => {
  const isFirst = !selectObserverEntry(getState());
  dispatch(resolveEntryRequest(isFirst, interviewToken));

  const succeed = response => {
    dispatch(resolveEntrySuccess(response, interviewToken));
    return response;
  };
  const fail = (e, opts) => {
    dispatch(resolveEntryError(toEntryError(e, opts), interviewToken));
    return null;
  };

  // ⚠ Each ATTEMPT gets its own timeout, the retry included — a budget spanning both would let a
  // hung retry inherit whatever the first attempt had left, which for a slow 401 is nearly nothing.
  return withEntryTimeout(joinSkillFitInterviewRoomAsObserver(interviewToken))
    .then(succeed)
    .catch(e => {
      // 401 = the handoff token expired or was replayed. Retried EXACTLY once — the retry mints a
      // fresh one — and then presented as 403. No other status is retried, and a
      // timeout carries no status at all, so it lands in the transient bucket rather than here.
      if (!e || e.status !== 401) {
        return fail(e);
      }
      return withEntryTimeout(joinSkillFitInterviewRoomAsObserver(interviewToken))
        .then(succeed)
        .catch(retryError => fail(retryError, { after401Retry: true }));
    });
};

/**
 * Ask the server what this interview is CALLED, independently of the door into it.
 *
 * ⭐ Fire-and-forget by construction: it resolves whatever happens, and a rejection is recorded and
 * dropped. ⛔ Nothing may await it — the moment the advance does, a slow marketplace is back on a
 * path that can cost somebody their interview, which is the exact defect this split removed.
 *
 * ⛔ Never put this on a timer. Two live marketplace lookups for three facts that do not change
 * while an interview runs is a once-per-screen cost; on the readiness tick's cadence it is the very
 * load whose slowness locked a recruiter out for a whole sitting.
 *
 * ⛔ It carries NO entry timeout. `withEntryTimeout` exists so a wedged server cannot leave the
 * proctor waiting on a door that will never open; this call opens no door, and abandoning it early
 * would only trade a late name for no name.
 */
export const fetchInterviewDisplay = interviewToken => dispatch => {
  // ⛔ A read with no token is not a read of "this interview" — it is a 404 the screen would then
  // have to explain. Say nothing instead; the dashes are already the honest answer.
  if (!interviewToken) return Promise.resolve();
  dispatch({ type: DISPLAY_REQUEST });
  return fetchSkillFitInterviewRoomDisplay(interviewToken)
    .then(fields => {
      dispatch({ type: DISPLAY_SUCCESS, payload: fields || null, meta: { token: interviewToken } });
    })
    .catch(e => {
      dispatch({ type: DISPLAY_ERROR, payload: storableError(e), meta: { token: interviewToken } });
    });
};

/**
 * The 5-second readiness tick: `{ status }` and nothing else.
 *
 * ⛔ NEVER the join helper on a timer — that call redeems a single-use handoff
 * token and mints a participant token discarded on every tick but the last. ⛔ Nor the display read
 * on a timer: two live marketplace lookups per tick, for three facts that cannot change while the
 * proctor waits, is the load whose slowness caused the lock-out in the first place.
 *
 * ⚠ A terminal session ('completed' / 'failed') arrives here as a normal 200 body. It dispatches
 * SUCCESS like any other status; the loop's stop condition reads `isTerminalSessionStatus`. 403,
 * 404 and 410 (the invitation has expired) are the only refusals this endpoint issues.
 *
 * Resolves with the status, or `null` on failure. A failed tick ⛔ never blanks the resolved entry.
 */
export const pollObserverReadiness = interviewToken => dispatch => {
  // Timed out for the same reason as the entry resolve, though the consequence is milder: the poll
  // runs on `setInterval`, so a hung tick never blocks the next one. Without a timeout, though, a
  // wedged server accumulates one pending request every 5 seconds for as long as the proctor waits.
  return withEntryTimeout(fetchSkillFitObserverReadiness(interviewToken))
    .then(response => {
      dispatch(pollReadinessSuccess(response, undefined, interviewToken));
      return response && response.status != null ? response.status : null;
    })
    .catch(e => {
      dispatch(pollReadinessError(toEntryError(e), interviewToken));
      return null;
    });
};
